import { useCallback, useEffect, useState } from 'react';

import { JDSFABInternalProps } from './generated';

export interface FABInternalProps
  extends Omit<JDSFABInternalProps, 'onHover' | 'onHoverEnd'> {
  /**
   * Remove FAB from DOM/memory.
   */
  unmount?: boolean;
  /**
   * Extends the FAB when hovered if the text is available.
   */
  extendOnHover?: boolean;
  /**
   * FAB shrinks on scroll if the title is available.
   */
  shrinkOnScroll?: boolean;
  /**
   * Called at the start of the animation for hiding
   */
  onHide?: () => void;
  /**
   * Called the start of the animation for showing.
   */
  onShow?: () => void;
  /**
   * Called at the end of unmounting.
   */
  onUnmount?: () => void;
}

export const withLogic =
  (
    Component: React.ComponentType<FABInternalProps>
  ): React.FC<FABInternalProps> =>
  (props: FABInternalProps) => {
    const [scroll, setScroll] = useState(false);
    const [hovered, setHovered] = useState(false);

    const { extend, extendOnHover, shrinkOnScroll, onHide, onShow, onUnmount } =
      props;

    const scrollListener = useCallback(
      (ev: Event) => {
        if (shrinkOnScroll)
          setScroll((ev.currentTarget as Window).scrollY !== 0);
      },
      [shrinkOnScroll]
    );

    useEffect(() => {
      return () => {
        onUnmount?.();
      };
    }, [onUnmount]);

    useEffect(() => {
      window.addEventListener('scroll', scrollListener);
      return () => {
        window.removeEventListener('scroll', scrollListener);
      };
    }, [scrollListener]);

    const internalExtend = useCallback(() => {
      if (!props.title) return false;
      if (scroll) {
        if (shrinkOnScroll) {
          onHide?.();
          return false;
        }
      }
      if (extend) {
        onShow?.();
        return true;
      }
      if (hovered) {
        onShow?.();
      }
      return hovered;
    }, [extend, scroll, hovered, onHide, onShow, shrinkOnScroll]);
    return (
      <Component
        {...{
          ...props,
          className: 'fabinternal-pointer',
          extend: internalExtend(),
          _hasTitle: Boolean(props.title),
          onHover: (ev: React.MouseEvent<HTMLDivElement>) => {
            ev.stopPropagation();
            if (extendOnHover) setHovered(true);
          },
          onHoverEnd: (ev: React.MouseEvent<HTMLDivElement>) => {
            ev.stopPropagation();
            setHovered(false);
          }
        }}
      />
    );
  };
